import { Button, Grid, InputAdornment } from "@mui/material";
import React, { ReactNode, useCallback, useContext, useState } from "react";
import { TOAST_TYPE, ToastContext } from "context/toastContext";
import { lookupPlate, lookupVIN } from "api";
import { useTranslation } from "react-i18next";
import { AppContext } from "context/appContext";
import { LoadingButton } from "@mui/lab";
import { Vehicle } from "types";
import VerifyVINDialog from "pages/jobs/create/verifyVINDialog";
import VRCameraDialog from "pages/jobs/VRCameraDialog";
import AuturaTextField from "components/inputs/AuturaTextField";
import AuturaStateField from "components/inputs/AuturaStateField";

export enum SearchType {
  NONE = "",
  VIN = "VIN",
  PLATE = "Plate",
  CAMERA = "Camera",
}

export interface VehicleLookupProps {
  searching: boolean;
  setSearching: (searching: boolean) => void;
  mobile?: boolean;
  autofillVehicle: (vehicle: Vehicle) => void;
}

const VehicleLookup = ({
  searching,
  setSearching,
  mobile = false,
  autofillVehicle,
}: VehicleLookupProps): ReactNode => {
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);

  const {
    state: { tenant },
  } = useContext(AppContext);
  const [plateNumber, setPlateNumber] = useState<string>("");
  const [plateState, setPlateState] = useState<string>("");
  const [vin, setVin] = useState<string>("");
  const [showDialog, setShowDialog] = useState<SearchType>(SearchType.NONE);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);

  const requiredVINVerify = tenant.data?.verifyNumberCharactersVIN;

  const handleVINLookup = useCallback(async () => {
    setSearching(true);
    try {
      const response = await lookupVIN(vin);
      if (response.ok) {
        const vehicle = await response.json();
        vehicle.vin = vin;
        autofillVehicle(vehicle);
      } else {
        throw new Error(
          `Error looking up vehicle by vin: ${response.statusText}`,
        );
      }
    } catch (e) {
      const error = e as Error;
      displayToast({ type: TOAST_TYPE.ERROR, message: error.message });
    } finally {
      setSearching(false);
    }
  }, [vin, autofillVehicle, displayToast, setSearching]);

  const handlePlateLookup = useCallback(async () => {
    setSearching(true);
    try {
      if (!plateState) throw new Error("Plate state is required");
      const response = await lookupPlate({
        state: plateState,
        number: plateNumber,
      });
      if (response.ok) {
        const vehicle: Vehicle = await response.json();
        setVehicle(vehicle);
        vehicle.plate = {
          state: plateState,
          number: plateNumber,
        };
        if (
          vehicle.vin &&
          vehicle.vin.length > 0 &&
          tenant.data?.verifyNumberCharactersVIN
        ) {
          setShowDialog(SearchType.VIN);
        } else {
          autofillVehicle(vehicle);
        }
      } else {
        throw new Error(
          `Error looking up vehicle by plate: ${response.statusText}`,
        );
      }
    } catch (e) {
      const error = e as Error;
      displayToast({ type: TOAST_TYPE.WARNING, message: error.message });
    } finally {
      setSearching(false);
    }
  }, [plateNumber, plateState, autofillVehicle, displayToast, setSearching]);

  const handleCameraDialogClose = useCallback((error: string) => {
    setShowDialog(SearchType.NONE);
    if (error) {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: error,
      });
    }
  }, []);

  return (
    <React.Fragment>
      {showDialog === SearchType.CAMERA && (
        <VRCameraDialog
          open={showDialog === SearchType.CAMERA}
          handleClose={handleCameraDialogClose}
        />
      )}
      {showDialog === SearchType.VIN && (
        <VerifyVINDialog
          open={showDialog === SearchType.VIN}
          setOpen={(o: boolean) =>
            setShowDialog(o ? SearchType.VIN : SearchType.NONE)
          }
          charactersToVerify={requiredVINVerify as number}
          vehicle={vehicle as Vehicle}
        />
      )}
      <Grid container width={"100%"} spacing={1.5}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <AuturaTextField
            name="vehicle.plateNumber"
            label={t("buttons.plate_lookup", { ns: "jobs" })}
            fullWidth
            disabled={searching}
            type="text"
            value={plateNumber || ""}
            onChange={(e) => {
              setPlateNumber(e.target.value.toUpperCase());
            }}
            helperText={t("helpers.plate_lookup", { ns: "jobs" })}
            InputSx={{
              px: 0,
            }}
            startAdornment={
              <InputAdornment
                position="start"
                sx={{ borderRight: "1px solid" }}
              >
                <AuturaStateField
                  sx={{
                    backgroundColor: "white",
                    width: 100,
                    pl: 2,
                    borderRight: "1px solid",
                  }}
                  variant="standard"
                  disableUnderline
                  label={""}
                  stateCode={plateState || ""}
                  setStateCode={(stateCode: string) => {
                    setPlateState(stateCode);
                  }}
                />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <LoadingButton
                  sx={{
                    px: 3,
                    py: "13px",
                    m: 0,
                    borderRadius: 0,
                    borderLeft: "1px solid",
                  }}
                  variant="contained"
                  color="secondary"
                  loading={searching}
                  disabled={searching || !plateNumber || !plateState}
                  onClick={handlePlateLookup}
                >
                  {t("search")}
                </LoadingButton>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            lg: 3,
          }}
        >
          <AuturaTextField
            name="vehicle.vin"
            label={t("buttons.vin_lookup", { ns: "jobs" })}
            sx={{ backgroundColor: "white" }}
            value={vin}
            helperText={t("helpers.vin_lookup", { ns: "jobs" })}
            onChange={(e) => {
              setVin(e.target.value.toUpperCase());
            }}
            InputSx={{
              pr: 0,
            }}
            endAdornment={
              <InputAdornment position="end" sx={{ m: 0, p: 0 }}>
                <LoadingButton
                  sx={{
                    px: 3,
                    py: "13px",
                    m: 0,
                    borderRadius: 0,
                    borderLeft: "1px solid",
                  }}
                  fullWidth
                  variant="contained"
                  color="secondary"
                  loading={searching}
                  disabled={searching || !vin}
                  onClick={handleVINLookup}
                >
                  {t("search")}
                </LoadingButton>
              </InputAdornment>
            }
          />
        </Grid>
        {mobile && (
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <Button onClick={() => setShowDialog(SearchType.CAMERA)}>
              {t("buttons.scan_plate", { ns: "jobs" })}
            </Button>
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

export default VehicleLookup;
